/**
 * Paging over the staff API's cursors.
 *
 * `_listing()` returns an opaque `next` — a base64 LastEvaluatedKey — and a
 * `cap` of 200 rows per page. So paging here walks the whole table rather than
 * one arbitrary slice, which matters more than it sounds: the old clips list
 * took one scan page and filtered `kind` in Python AFTER cutting it, so
 * `?kind=clip` returned however many clips happened to fall in the first 200
 * rows and called that all of them.
 *
 * Two honesty rules this file exists to enforce.
 *
 * `count` is the rows on THIS PAGE and never a total — a scan has no cheap
 * total — so the pager says "page 3 · 50 rows · more available" and never
 * "51–100 of 214", which would be a number nobody computed.
 *
 * And `scan.reason` is rendered wherever it appears. It is the handler saying
 * "this table had no index to read from, so newest-first is true of this page
 * and not of the table". Without it an operator sorting by date and paging
 * watches the dates jump and concludes the console is broken.
 */
import { useState } from 'react';
import type { Listing } from '../api';
import { useAsync, type AsyncState } from './async';

export type Cursored<T> = {
  result: AsyncState<Listing<T>>;
  page: number;
  canPrev: boolean;
  canNext: boolean;
  prev: () => void;
  next: () => void;
  reload: () => void;
};

export function useCursorList<T>(
  fetchPage: (cursor?: string) => Promise<Listing<T>>,
  deps: ReadonlyArray<unknown>,
): Cursored<T> {
  const key = JSON.stringify(deps);
  // The cursor stack, and which rung of it we are on. Held together in one
  // state object with the query key so that a filter change resets both in the
  // same render — a cursor from the previous query applied to the new one is a
  // page of somebody else's rows.
  const [nav, setNav] = useState<{
    key: string;
    cursors: Array<string | undefined>;
    page: number;
  }>({ key, cursors: [undefined], page: 0 });

  if (nav.key !== key) {
    // Set during render on purpose. The effect version fires a fetch with the
    // stale page first and then a second with the reset one, which on this
    // surface means two scans of a production table per keystroke.
    setNav({ key, cursors: [undefined], page: 0 });
  }

  const cursor = nav.key === key ? nav.cursors[nav.page] : undefined;
  const page = nav.key === key ? nav.page : 0;
  const { result, reload } = useAsync(() => fetchPage(cursor), [key, page]);

  const next = () => {
    if (result.state !== 'ok' || !result.value.next) return;
    const token = result.value.next;
    setNav((prev) => {
      const cursors = prev.cursors.slice(0, prev.page + 1);
      cursors[prev.page + 1] = token;
      return { ...prev, cursors, page: prev.page + 1 };
    });
  };

  return {
    result,
    page,
    canPrev: page > 0,
    canNext: result.state === 'ok' && Boolean(result.value.next),
    prev: () => setNav((p) => ({ ...p, page: Math.max(0, p.page - 1) })),
    next,
    reload,
  };
}

export function Pager<T>({
  list,
  what,
  shown,
}: {
  list: Cursored<T>;
  what: string;
  /** Rows actually rendered, after any browser-side filtering. Shown alongside
   *  the server's count when they differ, because "50 rows, 3 shown" is a very
   *  different page from "3 rows". */
  shown?: number;
}) {
  const count = list.result.state === 'ok' ? list.result.value.count : null;
  return (
    <div className="pager">
      <button onClick={list.prev} disabled={!list.canPrev} aria-label="previous page">
        ‹
      </button>
      <span className="nowrap">
        page {list.page + 1}
        {count !== null && (
          <>
            {' · '}
            {shown !== undefined && shown !== count ? `${shown} of ${count}` : count} {what}
          </>
        )}
        {list.canNext ? ' · more available' : ''}
      </span>
      <button onClick={list.next} disabled={!list.canNext} aria-label="next page">
        ›
      </button>
      <button onClick={list.reload} title="re-run this query">
        ↻
      </button>
    </div>
  );
}

/** The handler explaining that this list came from a scan, and what that costs
 *  the ordering. Rendered only when the API actually said so. */
export function ScanNote<T>({ listing }: { listing: Listing<T> }) {
  if (!listing.scan?.reason) return null;
  return (
    <p className="muted small">
      Ordered within this page only — {listing.scan.reason}. Paging forward can
      show older rows above newer ones.
    </p>
  );
}
